/**
 * My Work's `Customize` drawer (M5.C3, `specs/M7-C3-inbox-my-work.md` §2.3).
 *
 * Measured on monday: a **right drawer 400 × 852 at (1040, 89)**, a 24 px/500 title, three tabs
 * `General | Table | Calendar` (90 / 72 / 98 × 32 at y 167) and, in `General`, six section rows
 * — *People · Boards · Status Column · Date Column · Priority Column · Additional settings* —
 * plus the single toggle **`Hide done items`** at y 540.
 *
 * monday's drawer **saves live**; there is no Cancel, and this one does the same.
 */
import { useState } from 'react';
import { useOverlayDismiss } from '@elizmonday/ui';
import type { MyWorkSettingsView } from './types';

export interface CustomizeDrawerProps {
  settings: MyWorkSettingsView;
  boards: { id: string; name: string; count: number }[];
  onClose: () => void;
  onSave: (patch: Partial<MyWorkSettingsView>) => void;
}

const TABS = ['General', 'Table', 'Calendar'] as const;

export function CustomizeDrawer({ settings, boards, onClose, onSave }: CustomizeDrawerProps) {
  const [tab, setTab] = useState<(typeof TABS)[number]>('General');
  const selectedBoards = settings.boardIds;

  /*
   * P-013. The drawer used to close on Escape only while focus was inside it, and closing left
   * `document.activeElement` on `<body>` — the sweep caught it on the Table and Calendar tabs,
   * where the hint paragraph reads as an overlay of its own. The hook is the shared contract:
   * Escape closes wherever focus is, and focus returns to the *Customize* button that opened it.
   */
  useOverlayDismiss({ onClose });

  const toggleBoard = (id: string) => {
    const current = selectedBoards ?? boards.map((board) => board.id);
    const next = current.includes(id) ? current.filter((value) => value !== id) : [...current, id];
    onSave({ boardIds: next.length === boards.length ? null : next });
  };

  return (
    <aside
      className="ez-mywork-drawer"
      role="dialog"
      aria-label="Customize"
      data-testid="my-work-drawer"
    >
      <header className="ez-mywork-drawer__head">
        <h2 className="ez-mywork-drawer__title">Customize</h2>
        <button
          type="button"
          className="ez-mywork-drawer__close"
          aria-label="Close"
          data-testid="my-work-drawer-close"
          onClick={onClose}
        >
          ✕
        </button>
      </header>

      <div className="ez-mywork-drawer__tabs" role="tablist" aria-label="Customize sections">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={`ez-mywork-drawer__tab${tab === name ? ' is-selected' : ''}`}
            data-testid={`my-work-drawer-tab-${name.toLowerCase()}`}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>

      {tab !== 'General' ? (
        <p className="ez-mywork-drawer__hint" data-testid="my-work-drawer-notyet">
          The {tab} tab of monday&apos;s Customize drawer is not built yet. Everything the Table view
          reads lives under General.
        </p>
      ) : (
        <div className="ez-mywork-drawer__body">
          <section className="ez-mywork-drawer__section">
            <h3>People</h3>
            <p className="ez-mywork-drawer__hint">
              This page always shows your own work. Showing somebody else&apos;s arrives with M8&apos;s
              team management.
            </p>
          </section>

          <section className="ez-mywork-drawer__section">
            <h3>Boards</h3>
            <div className="ez-mywork-drawer__boards" data-testid="my-work-drawer-boards">
              {boards.length === 0 ? (
                <p className="ez-mywork-drawer__hint">No boards contribute items yet.</p>
              ) : (
                boards.map((board) => {
                  const on = selectedBoards === null || selectedBoards.includes(board.id);
                  return (
                    <label key={board.id} className="ez-mywork-drawer__row">
                      <input
                        type="checkbox"
                        checked={on}
                        data-testid={`my-work-drawer-board-${board.id}`}
                        onChange={() => toggleBoard(board.id)}
                      />
                      <span>{board.name}</span>
                      <span className="ez-mywork-drawer__count">{board.count}</span>
                    </label>
                  );
                })
              )}
            </div>
          </section>

          {/*
            monday's three column pickers are per board. Until a board is chosen there is
            nothing to pick, so the rows state the rule they follow rather than rendering an
            empty select — the rule itself is the product behaviour (spec §5.1 steps 3–5).
          */}
          {(['Status Column', 'Date Column', 'Priority Column'] as const).map((label) => (
            <section key={label} className="ez-mywork-drawer__section">
              <h3>{label}</h3>
              <p className="ez-mywork-drawer__hint">
                {label === 'Date Column'
                  ? 'The first date (or timeline) column of each board decides its bucket.'
                  : label === 'Status Column'
                    ? 'The first status column of each board decides what counts as done.'
                    : 'The first status column whose title mentions priority fills this cell.'}
              </p>
            </section>
          ))}

          <section className="ez-mywork-drawer__section">
            <h3>Additional settings</h3>
            <label className="ez-mywork-drawer__row">
              <input
                type="checkbox"
                checked={settings.hideDone}
                data-testid="my-work-hide-done"
                onChange={(event) => onSave({ hideDone: event.target.checked })}
              />
              <span>Hide done items</span>
            </label>
          </section>
        </div>
      )}
    </aside>
  );
}
